import React from 'react';
import dayjs from 'dayjs';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import usePurchaseOrderTypeById from '../../../../../../store/ReactQueries/procurements/purchaseOrderTypes/usePurchaseOrderTypeById';
import { getRPRStatusString } from '../../../../../../enums/PRStatus';
import { getRFPStatusString } from '../../../../../../enums/RFPStatus';
import { getRFQStatusString } from '../../../../../../enums/RFQStatus';
import { useDarkModeStore } from '../../../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../../../store/useLanguageStore';
import { NewAddComponentType } from '../../../../../../common/CustomDataGridStyle';
import { darkModeBackgroud, darkModeBackgroudInputs } from '../../../../../../utils/constants';
import { Modal, ModalContent, StyledBackdrop } from '../../../../../../common/TopBar/Buttons/SearchTopBarModal';

interface Item {
    name: string;
    value: string;
}

interface PRModalProps<T extends { [key: string]: any }> {
    form: T;
    open: {
        add: boolean;
        edit: boolean;
        view: boolean;
    };
    newAddComponent: NewAddComponentType;
    setOpen: React.Dispatch<React.SetStateAction<{
        add: boolean;
        edit: boolean;
        view: boolean;
    }>>;

}

const PRModal = <T extends { [key: string]: any }>({
    open,
    form,
    newAddComponent,
    setOpen,
}: PRModalProps<T>) => {

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: purchaseOrderTypeById } = usePurchaseOrderTypeById(form.purchaseOrderTypeId ?? '');

    const generateVendor = form.vendors?.map((v: any) => v.vendor?.name ?? '').join(', ');
    const generateEvaluationCriteria = form.evaluationCriteria?.map((e: any) => e.rfpEvaluationCriteria.title).join(', ');

    const handleClose = () => {
        setOpen({
            ...open,
            add: false,
            edit: false,
            view: false
        })
    }

    const PRData: Item[] = [
        { name: 'Code', value: form.code },
        { name: 'Description', value: form.description },
        { name: 'Quantity', value: form.quantity },
        { name: 'PurchaseOrderType', value: purchaseOrderTypeById?.title ?? '' },
        { name: 'Reason', value: form.reason },
        { name: 'Status', value: getRPRStatusString(form.status) },
        { name: 'Urgent', value: form.isUrgent },
    ];

    const RFPData: Item[] = [
        { name: 'Code', value: form.code },
        { name: 'Description', value: form.description },
        { name: 'DueDate', value: form.dueDate ?? form.duedate ? dayjs(form.dueDate ?? form.duedate).format('DD/MM/YYYY') : '' },
        { name: 'Budget', value: form.budget },
        { name: 'Status', value: getRFPStatusString(form.status) },
        { name: 'Vendors', value: generateVendor },
        { name: 'EvaluationCriterias', value: generateEvaluationCriteria },
        { name: 'Urgent', value: form.isUrgent },
    ];

    const RFQData: Item[] = [
        { name: 'Code', value: form.code },
        { name: 'Description', value: form.description },
        { name: 'Quantity', value: form.quantity },
        { name: 'DueDate', value: form.dueDate ? dayjs(form.dueDate).format('DD/MM/YYYY') : '' },
        { name: 'Currency', value: form.currency },
        { name: 'UnitMeasure', value: form.unitMeasure },
        { name: 'Status', value: getRFQStatusString(form.status) },
        { name: 'Vendors', value: generateVendor },
        { name: 'Urgent', value: form.isUrgent },
    ]

    const renderData = () => {
        if (newAddComponent === 'PurchaseRequests') return PRData;
        else if (newAddComponent === 'RequestForProposals') return RFPData;
        else if (newAddComponent === 'RequestForQuotations') return RFQData;
    }

    return <Modal
        open={true}
        onClose={handleClose}
        slots={{ backdrop: StyledBackdrop }}
    >
        <ModalContent
            sx={{
                width: 850,
                top: '50%',
                left: '50%',
                zIndex: 1300,
                position: 'fixed',
                pointerEvents: "auto",
                transform: 'translate(-50%, -50%)',
                backgroundColor: darkMode ? darkModeBackgroud : 'white',
            }}
        >
            <div>
                <div className="d-flex flex-row align-items-center ps-3 pe-3 pt-3">
                    <h4 className={`${darkMode ? 'text-white' : 'text-muted'} text-truncate`} style={{ maxWidth: 800 }}>
                        {language[newAddComponent] ?? newAddComponent}
                    </h4>
                    <IconButton
                        aria-label="Close Modal"
                        title="Close Modal"
                        size="small"
                        color={darkMode ? "info" : "default"}
                        className="ms-auto"
                        onClick={handleClose}
                    >
                        <ClearIcon />
                    </IconButton>
                </div>

                <hr className={`${darkMode && 'text-white opacity-100'} m-0 mt-2 p-0`} />
                <div className="table-responsive p-3" style={{ maxHeight: 600, overflow: 'auto' }}>
                    <table className="table table-bordered w-100">
                        <tbody>
                            {renderData()?.map(({ name, value, }, index) => (
                                <tr key={index} className="align-middle">
                                    <td
                                        className={`${darkMode ? 'text-white' : 'text-secondary'} fw-bold pt-3 pb-3`}
                                        style={{
                                            backgroundColor: darkMode ? darkModeBackgroudInputs : '#f8f9fa',
                                            width: '35%',
                                        }}
                                    >
                                        {language[name] ?? name}
                                    </td>

                                    <td
                                        className={`${darkMode ? 'text-white' : 'text-secondary'} pt-3 pb-3 ps-3`}
                                        style={{
                                            width: '65%',
                                            backgroundColor: darkMode ? darkModeBackgroudInputs : 'initial',
                                        }}
                                    >
                                        {
                                            name === 'Urgent'
                                                ? <span>
                                                    {
                                                        value === 'false'
                                                            ? language['No']
                                                            : language['Yes']
                                                    }
                                                </span>
                                                : value
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <hr className={`${darkMode && 'text-white opacity-100'} m-0 p-0`} />
            </div>
        </ModalContent>
    </Modal >
}
export default PRModal